<template>
  <b-card
    no-body
    class="card-browser-states equal-height-card"
  >
    <b-card-header
      class="d-flex flex-wrap justify-content-between align-items-center"
      style="gap: 10px;"
    >
      <div>
        <b-card-title class="chart-title d-flex align-items-center flex-wrap">
          <span class="mr-1">{{ $t('Ticket By Categories') }}</span>
          <tooltip-help :text="$t('Ticket distribution by status/type')" />
        </b-card-title>
        <b-card-text class="font-small-2" />
      </div>
      <b-button
        v-ripple.400="'rgba(113, 102, 240, 0.15)'"
        size="sm"
        variant="outline-primary"
        class="widgetrefresh-button d-flex align-items-center justify-content-center shadow-custom"
        :style="cardBorder"
        :disabled="!canRefresh"
        @click.stop="refreshWidget"
      >
        <feather-icon
          icon="RefreshCwIcon"
          class="mr-50 refresh-icon"
        />
        <span class="align-middle refresh-span" />
        <time-ago
          class="timeAgo"
          :datetime="lastRefreshTime ? new Date(parseInt(lastRefreshTime, 10)).toISOString() : lastUpdatedAt"
          refresh
          locale="en"
          :long="true"
          :style="{color: '#1C1F32'}"
        />
      </b-button>
    </b-card-header>

    <!-- body -->
    <b-card-body class="d-flex justify-content-center align-items-center mb-2 mr-0 chart-body">
      <vue-apex-charts
        :key="JSON.stringify(data.series)"
        :width="chartWidth"
        :options="chartOptions"
        :series="data.series"
        type="donut"
      />
    </b-card-body>
    <!--/ body -->
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardText,
  BCardBody,
  BButton,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { TimeAgo } from 'vue2-timeago'
import Ripple from 'vue-ripple-directive'
import RefreshMixin from '@/@core/mixins/ui/refresh'
import TooltipHelp from '../Dashboard/TooltipHelp.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardText,
    BCardBody,
    VueApexCharts,
    TimeAgo,
    BButton,
    TooltipHelp,
  },
  directives: {
    Ripple,
  },
  mixins: [RefreshMixin],
  props: {
    data: {
      type: Object,
      default: () => [],
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    queue: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      chartWidth: 540,
      lastRefreshTime: (JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {}).Categories || 0,
      now: Date.now(),
      timer: null,
    }
  },
  computed: {
    chartOptions() {
      return {
        chart: {
          type: 'donut',
          stacked: false,
          sparkline: { enabled: false },
          animations: { enabled: true },
        },
        plotOptions: {
          pie: {
            donut: {
              size: '75%',
            },
          },
        },
        labels: this.data.labels || [],
        colors: ['#cbeccb', '#ebc4c4', '#cbe6f2', '#f2e9cb', '#e9cbf2'],
        dataLabels: { enabled: false },
        states: {
          hover: { filter: { type: 'none', value: 0 } },
          active: { filter: { type: 'none', value: 0 } },
        },
        stroke: { show: true, width: 0 },
        legend: {
          position: 'right',
          horizontalAlign: 'center',
          offsetY: 40,
          offsetX: 20,
          fontSize: '14px',
          fontWeight: 600,
          itemMargin: {
            vertical: 8,
            horizontal: 10,
          },
          formatter: (seriesName, opts) => {
            const series = this.data.series || []
            const total = series.reduce((a, b) => Number(a) + Number(b), 0)
            const index = opts.seriesIndex
            const value = series[index]
            const percent = total ? ((value / total) * 100).toFixed(1) : 0
            return `${seriesName} - ${percent}%`
          },
        },
        responsive: [
          {
            breakpoint: 1300,
            options: {
              chart: { width: 450 },
              legend: { offsetX: 0 },
            },
          },
          {
            breakpoint: 1200,
            options: {
              chart: { width: 380 },
              legend: {
                position: 'bottom',
                horizontalAlign: 'center',
                offsetY: 15,
                offsetX: 0,
              },
            },
          },
          {
            breakpoint: 991,
            options: {
              chart: { width: 450 },
              legend: {
                position: 'bottom',
                horizontalAlign: 'center',
                offsetY: 15,
              },
            },
          },
          {
            breakpoint: 576,
            options: {
              chart: { width: 320 },
              legend: {
                position: 'bottom',
                horizontalAlign: 'center',
                offsetY: 15,
              },
            },
          },
        ],
      }
    },
    cardBorder() {
      return {
        border: '1px solid var(--primary) !important',
        opacity: this.canRefresh ? 1 : 0.5,
      }
    },
    canRefresh() {
      const diff = this.now - parseInt(this.lastRefreshTime, 10)
      return diff >= 60000
    },
  },
  created() {
    this.timer = setInterval(() => {
      this.now = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  mounted() {
    const timestamps = JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {}
    if (!timestamps.Categories) {
      this.refreshWidget()
    }
  },
  methods: {
    refreshWidget() {
      if (!this.canRefresh) return
      const timestamp = Date.now()
      this.lastRefreshTime = timestamp
      const timestampsObj = JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {}
      timestampsObj.Categories = timestamp
      localStorage.setItem('ticketDashboardLastUpdatedAt', JSON.stringify(timestampsObj))
      this.$emit('refresh', this.queue)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../@core/scss/vue/pages/widget-refresh.scss';

.equal-height-card {
  min-height: 408px;
  display: flex;
  flex-direction: column;
  overflow: visible !important;
  position: relative;
  z-index: 10;
}

.card-browser-states {
  overflow: visible !important;
}

.chart-body {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible !important;
  position: relative;
  z-index: 10;
  padding: 20px;
}

::v-deep .apexcharts-tooltip {
  z-index: 9999 !important;
}

.refresh-icon {
  color: #1C1F32;
}
</style>
<style lang="scss">
.blue-tooltip .tooltip-inner {
  background-color: #0094FF !important;
  color: #fff !important;
  font-weight: 500;
  border-radius: 8px;
  padding: 6px 12px;
}
.blue-tooltip .arrow::before {
  border-top-color: #0094FF !important;
  border-bottom-color: #0094FF !important;
}
</style>
